import React, { useState, useEffect } from 'react';
import api from '../../api/axios';
import { useToast } from '../../context/ToastContext';

const Commercials = ({ enterpriseId, onSave, createdOn }) => {
  const toast = useToast();

  // State for commercial config
  const [commercials, setCommercials] = useState(null);
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [error, setError] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;

  // Edit mode state
  const [editValues, setEditValues] = useState({
    perUserPerMonthMaker: '',
    perUserPerMonthAdmin: '',
    loadingChargesPercent: '',
    upiChargesPerTransaction: '',
    additionalCharge: '',
    perUserMonthlyMmg: '',
    perMonthLoadingMmg: '',
    perMonthOverallMmg: '',
    freeUpiTransactions: '0',
    freeAdminUsers: '0',
  });
  const [changeReason, setChangeReason] = useState('');
  const [accountType, setAccountType] = useState(''); // DEMO | ZEEROW | TESTING | INCONSISTENT | CLOSED | ''
  const [closedAt, setClosedAt] = useState(''); // only used when accountType === 'CLOSED'
  const [showDemoPeriod, setShowDemoPeriod] = useState(false);

  // Slab state
  const [useSlab, setUseSlab] = useState(false);
  const [makerSlabs, setMakerSlabs] = useState([]);

  const emptySlabRow = () => ({ fromCount: '', toCount: '', chargeType: 'FIXED', rate: '' });

  const handleSlabChange = (idx, field, value) => {
    setMakerSlabs(prev => prev.map((s, i) => i === idx ? { ...s, [field]: value } : s));
  };

  const handleAddSlab = () => {
    setMakerSlabs(prev => {
      const newRow = emptySlabRow();
      // Auto-set fromCount = last toCount + 1 (if possible)
      if (prev.length > 0) {
        const lastTo = prev[prev.length - 1].toCount;
        newRow.fromCount = lastTo !== '' && lastTo !== null ? String(Number(lastTo) + 1) : '';
      }
      return [...prev, newRow];
    });
  };

  const handleRemoveSlab = (idx) => {
    setMakerSlabs(prev => prev.filter((_, i) => i !== idx));
  };

  const validateSlabs = () => {
    for (let i = 0; i < makerSlabs.length; i++) {
      const s = makerSlabs[i];
      if (s.fromCount === '' || s.fromCount === null) return `Slab ${i + 1}: From count is required`;
      if (Number(s.fromCount) < 0) return `Slab ${i + 1}: From count must be >= 0`;
      if (i < makerSlabs.length - 1 && (s.toCount === '' || s.toCount === null)) return `Slab ${i + 1}: To count is required (only the last slab can be unlimited)`;
      if (s.toCount !== '' && s.toCount !== null && Number(s.toCount) < Number(s.fromCount)) return `Slab ${i + 1}: To count must be >= From count`;
      if (s.rate === '' || s.rate === null) return `Slab ${i + 1}: Rate is required`;
      if (Number(s.rate) < 0) return `Slab ${i + 1}: Rate must be >= 0`;
      if (!s.chargeType) return `Slab ${i + 1}: Charge type is required`;
    }
    return null;
  };

  const ACCOUNT_TYPES = [
    { value: 'DEMO', label: 'Demo Account', color: 'blue', desc: 'Testing/demo purposes' },
    { value: 'ZEEROW', label: 'Zeerow Account', color: 'purple', desc: 'Zeerow program' },
    { value: 'TESTING', label: 'Testing Account', color: 'orange', desc: 'Internal testing' },
    { value: 'INCONSISTENT', label: 'Inconsistent Commercial', color: 'red', desc: 'Commercial issues' },
    { value: 'ANNUAL', label: 'Annual Charges', color: 'green', desc: 'Billed annually — exempt from monthly billing' },
    { value: 'CLOSED', label: 'Closed Account', color: 'gray', desc: 'Enterprise permanently closed — irreversible' },
  ];

  // Fetch commercials config
  useEffect(() => {
    fetchCommercials();
    fetchHistory();
  }, [enterpriseId]);

  const fetchCommercials = async () => {
    try {
      setLoading(true);
      const response = await api.get(`/api/admin/enterprises/${enterpriseId}/commercials`);
      setCommercials(response.data);
      setError('');
    } catch (err) {
      if (err.response?.status === 404) {
        setCommercials(null); // No config exists yet
        setError('');
      } else {
        setError(err.response?.data?.error || 'Failed to load commercials');
        toast.error(err.response?.data?.error || 'Failed to load commercials');
      }
    } finally {
      setLoading(false);
    }
  };

  const fetchHistory = async () => {
    try {
      const response = await api.get(`/api/admin/enterprises/${enterpriseId}/commercials/history`);
      setHistory(response.data);
    } catch (err) {
      // History may not exist if no changes yet
      setHistory([]);
    }
  };

  const handleEdit = () => {
    if (commercials) {
      setEditValues({
        perUserPerMonthMaker: commercials.perUserPerMonthMaker?.toString() || '0.00',
        perUserPerMonthAdmin: commercials.perUserPerMonthAdmin?.toString() || '0.00',
        loadingChargesPercent: commercials.loadingChargesPercent?.toString() || '0.00',
        upiChargesPerTransaction: commercials.upiChargesPerTransaction?.toString() || '0.00',
        additionalCharge: commercials.additionalCharge?.toString() || '0.00',
        perUserMonthlyMmg: commercials.perUserMonthlyMmg?.toString() || '0.00',
        perMonthLoadingMmg: commercials.perMonthLoadingMmg?.toString() || '0.00',
        perMonthOverallMmg: commercials.perMonthOverallMmg?.toString() || '0.00',
        freeUpiTransactions: (commercials.freeUpiTransactions ?? 0).toString(),
        freeAdminUsers: (commercials.freeAdminUsers ?? 0).toString(),
      });
      setAccountType(commercials.accountType || '');
      setClosedAt(commercials.closedAt || '');
      setChangeReason('');
      // Populate slab state from existing config
      const existingSlabs = (commercials.makerSlabs || []).map(s => ({
        fromCount: String(s.fromCount),
        toCount: s.toCount !== null && s.toCount !== undefined ? String(s.toCount) : '',
        chargeType: s.chargeType || 'FIXED',
        rate: String(s.rate),
      }));
      setUseSlab(existingSlabs.length > 0);
      setMakerSlabs(existingSlabs.length > 0 ? existingSlabs : [emptySlabRow()]);
      setIsEditing(true);
    }
  };

  const handleCancel = () => {
    setIsEditing(false);
    setChangeReason('');
    setAccountType(commercials?.accountType || '');
    setClosedAt(commercials?.closedAt || '');
    setUseSlab(false);
    setMakerSlabs([]);
    setError('');
  };

  const handleInputChange = (field, value) => {
    setEditValues(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      setError('');

      // Validate inputs
      const requestData = {
        perUserPerMonthMaker: parseFloat(editValues.perUserPerMonthMaker) || 0,
        perUserPerMonthAdmin: parseFloat(editValues.perUserPerMonthAdmin) || 0,
        loadingChargesPercent: parseFloat(editValues.loadingChargesPercent) || 0,
        upiChargesPerTransaction: parseFloat(editValues.upiChargesPerTransaction) || 0,
        additionalCharge: parseFloat(editValues.additionalCharge) || 0,
        perUserMonthlyMmg: parseFloat(editValues.perUserMonthlyMmg) || 0,
        perMonthLoadingMmg: parseFloat(editValues.perMonthLoadingMmg) || 0,
        perMonthOverallMmg: parseFloat(editValues.perMonthOverallMmg) || 0,
        freeUpiTransactions: parseInt(editValues.freeUpiTransactions, 10) || 0,
        freeAdminUsers: parseInt(editValues.freeAdminUsers, 10) || 0,
        changeReason: changeReason || null,
        accountType: accountType || null,
        ...(accountType === 'CLOSED' && closedAt ? { closedAt } : {}),
      };

      // Validate closedAt not before enterprise createdOn
      if (accountType === 'CLOSED' && closedAt && createdOn) {
        const createdOnDate = createdOn.substring(0, 10); // 'YYYY-MM-DD'
        if (closedAt < createdOnDate) {
          const msg = `Closed date cannot be before the enterprise creation date (${new Date(createdOnDate).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })})`;
          setError(msg);
          toast.error(msg);
          setSaving(false);
          return;
        }
      }

      // Validate ranges
      if (requestData.loadingChargesPercent < 0 || requestData.loadingChargesPercent > 100) {
        setError('Loading charges must be between 0 and 100%');
        toast.error('Loading charges must be between 0 and 100%');
        setSaving(false);
        return;
      }

      // Validate and attach maker slabs
      if (useSlab) {
        if (makerSlabs.length === 0) {
          setError('Add at least one slab row, or disable slab-based pricing.');
          toast.error('Add at least one slab row, or disable slab-based pricing.');
          setSaving(false);
          return;
        }
        const slabError = validateSlabs();
        if (slabError) {
          setError(slabError);
          toast.error(slabError);
          setSaving(false);
          return;
        }
        requestData.makerSlabs = makerSlabs.map((s, i) => ({
          fromCount: Number(s.fromCount),
          toCount: s.toCount !== '' && s.toCount !== null ? Number(s.toCount) : null,
          chargeType: s.chargeType,
          rate: parseFloat(s.rate),
        }));
      } else {
        requestData.makerSlabs = []; // Explicit empty = remove all slabs, use flat rate
      }

      if (commercials) {
        // Update existing
        const response = await api.post(
          `/api/admin/enterprises/${enterpriseId}/commercials/update`,
          requestData
        );
        setCommercials(response.data);
        toast.success('Commercials updated successfully');
      } else {
        // Create new
        const response = await api.post(
          `/api/admin/enterprises/${enterpriseId}/commercials`,
          requestData
        );
        setCommercials(response.data);
        toast.success('Commercials created successfully');
      }

      setIsEditing(false);
      setChangeReason('');
      await fetchHistory(); // Refresh history
      if (onSave) onSave(); // Refresh parent enterprise list
    } catch (err) {
      const errMsg = err.response?.data?.error || 'Failed to save commercials';
      setError(errMsg);
      toast.error(errMsg);
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center py-8">
        <p className="text-gray-500">Loading commercials...</p>
      </div>
    );
  }

  // Pagination for history
  const totalPages = Math.ceil(history.length / itemsPerPage);
  const paginatedHistory = history.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

  return (
    <div className="space-y-6">
      {/* Commercial Config Form */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h3 className="text-lg font-semibold text-gray-800">Billing Configuration</h3>
            <p className="text-sm text-gray-400 mt-1">Manage charges and fees for this enterprise</p>
          </div>
          {!isEditing && commercials && (
            commercials.accountType === 'CLOSED' ? (
              <span className="px-3 py-2 bg-gray-100 text-gray-500 rounded-lg text-sm font-medium cursor-not-allowed select-none">
                🔒 Account Closed
              </span>
            ) : commercials.accountType === 'ZEEROW' ? (
              <span className="px-3 py-2 bg-purple-50 text-purple-500 rounded-lg text-sm font-medium cursor-not-allowed select-none">
                Zeerow Billing
              </span>
            ) : (
              <button
                onClick={handleEdit}
                className="px-4 py-2 bg-blue-50 text-blue-600 rounded-lg text-sm font-medium hover:bg-blue-100 transition-colors"
              >
                Edit
              </button>
            )
          )}
        </div>

        {error && (
          <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg flex items-center justify-between">
            <p className="text-sm text-red-600">{error}</p>
            <button
              onClick={() => setError('')}
              className="text-red-500 hover:text-red-700"
            >
              ×
            </button>
          </div>
        )}

        {commercials ? (
          <>
            {/* Form Fields */}
            <div className="grid grid-cols-2 gap-4 mb-6">
              {/* Maker Charges */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Per User Per Month — Maker
                </label>
                {!isEditing ? (
                  <div className="px-3 py-2 bg-gray-50 border border-gray-200 rounded-lg text-gray-700">
                    ₹{parseFloat(commercials.perUserPerMonthMaker || 0).toFixed(2)}
                  </div>
                ) : (
                  <div className="relative">
                    <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm">₹</span>
                    <input
                      type="number"
                      step="0.01"
                      value={editValues.perUserPerMonthMaker}
                      onChange={(e) => handleInputChange('perUserPerMonthMaker', e.target.value)}
                      className="w-full border border-gray-200 rounded-lg pl-7 pr-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary/30"
                      placeholder="0.00"
                    />
                  </div>
                )}
              </div>

              {/* Admin Charges */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Per User Per Month — Admin
                </label>
                {!isEditing ? (
                  <div className="px-3 py-2 bg-gray-50 border border-gray-200 rounded-lg text-gray-700">
                    ₹{parseFloat(commercials.perUserPerMonthAdmin || 0).toFixed(2)}
                  </div>
                ) : (
                  <div className="relative">
                    <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm">₹</span>
                    <input
                      type="number"
                      step="0.01"
                      value={editValues.perUserPerMonthAdmin}
                      onChange={(e) => handleInputChange('perUserPerMonthAdmin', e.target.value)}
                      className="w-full border border-gray-200 rounded-lg pl-7 pr-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary/30"
                      placeholder="0.00"
                    />
                  </div>
                )}
              </div>

              {/* Loading Charges */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Loading Charges
                </label>
                {!isEditing ? (
                  <div className="px-3 py-2 bg-gray-50 border border-gray-200 rounded-lg text-gray-700">
                    {parseFloat(commercials.loadingChargesPercent || 0).toFixed(2)}%
                  </div>
                ) : (
                  <div className="relative">
                    <input
                      type="number"
                      step="0.01"
                      min="0"
                      max="100"
                      value={editValues.loadingChargesPercent}
                      onChange={(e) => handleInputChange('loadingChargesPercent', e.target.value)}
                      className="w-full border border-gray-200 rounded-lg pl-3 pr-8 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary/30"
                      placeholder="0.00"
                    />
                    <span className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm">%</span>
                  </div>
                )}
              </div>

              {/* UPI Charges */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  UPI Charges Per Transaction
                </label>
                {!isEditing ? (
                  <div className="px-3 py-2 bg-gray-50 border border-gray-200 rounded-lg text-gray-700">
                    ₹{parseFloat(commercials.upiChargesPerTransaction || 0).toFixed(2)}
                  </div>
                ) : (
                  <div className="relative">
                    <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm">₹</span>
                    <input
                      type="number"
                      step="0.01"
                      value={editValues.upiChargesPerTransaction}
                      onChange={(e) => handleInputChange('upiChargesPerTransaction', e.target.value)}
                      className="w-full border border-gray-200 rounded-lg pl-7 pr-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary/30"
                      placeholder="0.00"
                    />
                  </div>
                )}
              </div>

              {/* Free UPI Transactions */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Free UPI Transactions (Per Month)
                </label>
                {!isEditing ? (
                  <div className="px-3 py-2 bg-gray-50 border border-gray-200 rounded-lg text-gray-700">
                    {commercials.freeUpiTransactions ?? 0} txns
                  </div>
                ) : (
                  <input
                    type="number"
                    step="1"
                    min="0"
                    value={editValues.freeUpiTransactions}
                    onChange={(e) => handleInputChange('freeUpiTransactions', e.target.value)}
                    className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary/30"
                    placeholder="0"
                  />
                )}
                <p className="text-xs text-gray-400 mt-1">UPI txns not charged until this limit is exceeded</p>
              </div>

              {/* Free Admin Users */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Free Admin Users (Per Month)
                </label>
                {!isEditing ? (
                  <div className="px-3 py-2 bg-gray-50 border border-gray-200 rounded-lg text-gray-700">
                    {commercials.freeAdminUsers ?? 0} admins
                  </div>
                ) : (
                  <input
                    type="number"
                    step="1"
                    min="0"
                    value={editValues.freeAdminUsers}
                    onChange={(e) => handleInputChange('freeAdminUsers', e.target.value)}
                    className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary/30"
                    placeholder="0"
                  />
                )}
                <p className="text-xs text-gray-400 mt-1">Admin users not billed until this count is exceeded</p>
              </div>

              {/* Additional Charge */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Additional Charge (Per Month)
                </label>
                {!isEditing ? (
                  <div className="px-3 py-2 bg-gray-50 border border-gray-200 rounded-lg text-gray-700">
                    ₹{parseFloat(commercials.additionalCharge || 0).toFixed(2)}
                  </div>
                ) : (
                  <div className="relative">
                    <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm">₹</span>
                    <input
                      type="number"
                      step="0.01"
                      value={editValues.additionalCharge}
                      onChange={(e) => handleInputChange('additionalCharge', e.target.value)}
                      className="w-full border border-gray-200 rounded-lg pl-7 pr-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary/30"
                      placeholder="0.00"
                    />
                  </div>
                )}
              </div>

              {/* Per User Monthly MMG */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Per User Monthly MMG
                </label>
                {!isEditing ? (
                  <div className="px-3 py-2 bg-gray-50 border border-gray-200 rounded-lg text-gray-700">
                    ₹{parseFloat(commercials.perUserMonthlyMmg || 0).toFixed(2)}
                  </div>
                ) : (
                  <div className="relative">
                    <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm">₹</span>
                    <input
                      type="number"
                      step="0.01"
                      value={editValues.perUserMonthlyMmg}
                      onChange={(e) => handleInputChange('perUserMonthlyMmg', e.target.value)}
                      className="w-full border border-gray-200 rounded-lg pl-7 pr-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary/30"
                      placeholder="0.00"
                    />
                  </div>
                )}
              </div>

              {/* Per Month Loading MMG */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Per Month Loading MMG
                </label>
                {!isEditing ? (
                  <div className="px-3 py-2 bg-gray-50 border border-gray-200 rounded-lg text-gray-700">
                    ₹{parseFloat(commercials.perMonthLoadingMmg || 0).toFixed(2)}
                  </div>
                ) : (
                  <div className="relative">
                    <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm">₹</span>
                    <input
                      type="number"
                      step="0.01"
                      value={editValues.perMonthLoadingMmg}
                      onChange={(e) => handleInputChange('perMonthLoadingMmg', e.target.value)}
                      className="w-full border border-gray-200 rounded-lg pl-7 pr-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary/30"
                      placeholder="0.00"
                    />
                  </div>
                )}
              </div>

              {/* Per Month Overall MMG */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Per Month Overall MMG
                </label>
                {!isEditing ? (
                  <div className="px-3 py-2 bg-gray-50 border border-gray-200 rounded-lg text-gray-700">
                    ₹{parseFloat(commercials.perMonthOverallMmg || 0).toFixed(2)}
                  </div>
                ) : (
                  <div className="relative">
                    <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm">₹</span>
                    <input
                      type="number"
                      step="0.01"
                      value={editValues.perMonthOverallMmg}
                      onChange={(e) => handleInputChange('perMonthOverallMmg', e.target.value)}
                      className="w-full border border-gray-200 rounded-lg pl-7 pr-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary/30"
                      placeholder="0.00"
                    />
                  </div>
                )}
              </div>
            </div>

            {/* Maker Slabs Section */}
            {isEditing && (
              <div className="mb-6 border border-primary/10 rounded-xl p-4 bg-gray-50/50">
                <div className="flex items-center gap-3 mb-3">
                  <input
                    id="useSlab"
                    type="checkbox"
                    checked={useSlab}
                    onChange={e => {
                      setUseSlab(e.target.checked);
                      if (e.target.checked && makerSlabs.length === 0) setMakerSlabs([emptySlabRow()]);
                    }}
                    className="w-4 h-4 accent-primary cursor-pointer"
                  />
                  <label htmlFor="useSlab" className="text-sm font-semibold text-gray-700 cursor-pointer select-none">
                    Use slab-based maker pricing
                  </label>
                  <span className="text-xs text-gray-400">(overrides the flat rate above when enabled)</span>
                </div>

                {useSlab && (
                  <>
                    <div className="overflow-x-auto rounded-lg border border-primary/10">
                      <table className="w-full text-xs">
                        <thead>
                          <tr className="bg-primary/5 text-primary text-left">
                            <th className="px-3 py-2 font-semibold">From (users)</th>
                            <th className="px-3 py-2 font-semibold">To (blank = ∞)</th>
                            <th className="px-3 py-2 font-semibold">Type</th>
                            <th className="px-3 py-2 font-semibold">Rate (₹)</th>
                            <th className="px-3 py-2 font-semibold"></th>
                          </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-gray-100">
                          {makerSlabs.map((slab, idx) => {
                            const isLast = idx === makerSlabs.length - 1;
                            return (
                              <tr key={idx}>
                                <td className="px-2 py-1.5">
                                  <input
                                    type="number"
                                    min="0"
                                    step="1"
                                    value={slab.fromCount}
                                    onChange={e => handleSlabChange(idx, 'fromCount', e.target.value)}
                                    className="w-20 border border-gray-200 rounded-lg px-2 py-1 text-xs focus:outline-none focus:ring-2 focus:ring-primary/30"
                                    placeholder="0"
                                  />
                                </td>
                                <td className="px-2 py-1.5">
                                  {isLast ? (
                                    <span className="inline-flex items-center px-2 py-1 text-xs text-gray-400 bg-gray-50 border border-gray-200 rounded-lg">∞ (unlimited)</span>
                                  ) : (
                                    <input
                                      type="number"
                                      min="0"
                                      step="1"
                                      value={slab.toCount}
                                      onChange={e => handleSlabChange(idx, 'toCount', e.target.value)}
                                      className="w-20 border border-gray-200 rounded-lg px-2 py-1 text-xs focus:outline-none focus:ring-2 focus:ring-primary/30"
                                      placeholder="e.g. 99"
                                    />
                                  )}
                                </td>
                                <td className="px-2 py-1.5">
                                  <select
                                    value={slab.chargeType}
                                    onChange={e => handleSlabChange(idx, 'chargeType', e.target.value)}
                                    className="border border-gray-200 rounded-lg px-2 py-1 text-xs focus:outline-none focus:ring-2 focus:ring-primary/30"
                                  >
                                    <option value="FIXED">Fixed Amount</option>
                                    <option value="PER_USER">Per User</option>
                                  </select>
                                </td>
                                <td className="px-2 py-1.5">
                                  <div className="relative">
                                    <span className="absolute left-2 top-1/2 -translate-y-1/2 text-gray-400 text-xs">₹</span>
                                    <input
                                      type="number"
                                      min="0"
                                      step="0.01"
                                      value={slab.rate}
                                      onChange={e => handleSlabChange(idx, 'rate', e.target.value)}
                                      className="w-24 border border-gray-200 rounded-lg pl-5 pr-2 py-1 text-xs focus:outline-none focus:ring-2 focus:ring-primary/30"
                                      placeholder="0.00"
                                    />
                                  </div>
                                </td>
                                <td className="px-2 py-1.5">
                                  <button
                                    type="button"
                                    onClick={() => handleRemoveSlab(idx)}
                                    className="text-red-400 hover:text-red-600 text-sm font-bold leading-none px-1"
                                    title="Remove slab"
                                  >
                                    ✕
                                  </button>
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                    <div className="flex items-center gap-3 mt-2">
                      <button
                        type="button"
                        onClick={handleAddSlab}
                        className="text-xs text-primary font-semibold hover:underline"
                      >
                        + Add Slab
                      </button>
                      <p className="text-xs text-gray-400">Last slab's "To" is always unlimited. Slabs are matched top-down.</p>
                    </div>
                    <div className="mt-2 p-2 bg-blue-50 border border-blue-100 rounded-lg text-xs text-blue-700">
                      <strong>Fixed Amount:</strong> Billed as a flat fee regardless of user count.&nbsp;
                      <strong>Per User:</strong> Rate × actual maker count.
                    </div>
                  </>
                )}
                {!useSlab && (
                  <p className="text-xs text-gray-400 mt-1">Flat rate above applies. Enable to set tiered pricing.</p>
                )}
              </div>
            )}

            {/* Slab summary in view mode */}
            {!isEditing && commercials?.makerSlabs?.length > 0 && (
              <div className="mb-6 border border-primary/10 rounded-xl p-4 bg-gray-50/50">
                <p className="text-sm font-semibold text-gray-700 mb-2">Maker Pricing Slabs</p>
                <div className="overflow-x-auto rounded-lg border border-primary/10">
                  <table className="w-full text-xs">
                    <thead>
                      <tr className="bg-primary/5 text-primary text-left">
                        <th className="px-3 py-2 font-semibold">From</th>
                        <th className="px-3 py-2 font-semibold">To</th>
                        <th className="px-3 py-2 font-semibold">Type</th>
                        <th className="px-3 py-2 font-semibold">Rate</th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-100">
                      {commercials.makerSlabs.map((s, i) => (
                        <tr key={i} className="hover:bg-primary/5">
                          <td className="px-3 py-2 text-gray-700">{s.fromCount}</td>
                          <td className="px-3 py-2 text-gray-700">{s.toCount !== null && s.toCount !== undefined ? s.toCount : '∞'}</td>
                          <td className="px-3 py-2 text-gray-700">{s.chargeType === 'FIXED' ? 'Fixed Amount' : 'Per User'}</td>
                          <td className="px-3 py-2 text-gray-700">₹{parseFloat(s.rate || 0).toFixed(2)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {/* Account Classification */}
            <div className="mb-6">
              <div className="flex items-center gap-2 mb-3">
                <label className="text-sm font-medium text-gray-700">Account Classification</label>
                {!isEditing && commercials?.accountType && (
                  <div className="flex flex-col items-start gap-1.5">
                    <span className={`inline-flex flex-col items-center px-3 py-1.5 rounded-lg text-xs font-semibold leading-snug ${
                      commercials.accountType === 'DEMO'         ? 'bg-blue-100 text-blue-700' :
                      commercials.accountType === 'ZEEROW'       ? 'bg-purple-100 text-purple-700' :
                      commercials.accountType === 'TESTING'      ? 'bg-orange-100 text-orange-700' :
                      commercials.accountType === 'ANNUAL'       ? 'bg-green-100 text-green-700' :
                      commercials.accountType === 'CLOSED'       ? 'bg-gray-200 text-gray-600' :
                      'bg-red-100 text-red-700'
                    }`}>
                      {commercials.accountType === 'DEMO'    ? <><span>Demo</span><span>Account</span></> :
                       commercials.accountType === 'ZEEROW'  ? <><span>Zeerow</span><span>Account</span></> :
                       commercials.accountType === 'TESTING' ? <><span>Testing</span><span>Account</span></> :
                       commercials.accountType === 'ANNUAL'  ? <><span>Annual</span><span>Charges</span></> :
                       commercials.accountType === 'CLOSED'  ? <><span>Closed</span><span>Account</span></> :
                       <><span>Inconsistent</span><span>Commercials</span></>}
                    </span>
                    {/* Closed On date — only for CLOSED accounts */}
                    {commercials.accountType === 'CLOSED' && commercials.closedAt && (
                      <p className="text-xs text-gray-500 font-medium">
                        Closed on:{' '}
                        {new Date(commercials.closedAt).toLocaleDateString('en-IN', {
                          day: '2-digit', month: 'short', year: 'numeric'
                        })}
                      </p>
                    )}

                    {/* Demo Period button — only for DEMO accounts */}
                    {commercials.accountType === 'DEMO' && (
                      <div className="relative">
                        <button
                          type="button"
                          onClick={() => setShowDemoPeriod(p => !p)}
                          className="text-xs text-blue-600 font-medium underline underline-offset-2 hover:text-blue-800 transition-colors"
                        >
                          Demo Period
                        </button>
                        {showDemoPeriod && (
                          <div className="fixed inset-0 z-10" onClick={() => setShowDemoPeriod(false)} />
                        )}
                        {showDemoPeriod && (() => {
                          // History stores NEW values. Walk DESC to find oldest consecutive DEMO entry
                          // (i.e. the entry that first set DEMO in the current unbroken streak).
                          const sorted = [...history].sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
                          let activationEntry = null;
                          for (const h of sorted) {
                            if (h.accountType === 'DEMO') {
                              activationEntry = h; // keep going — oldest DEMO in streak wins
                            } else {
                              break; // chain broken, stop
                            }
                          }
                          const activatedAt = activationEntry
                            ? new Date(activationEntry.createdAt)
                            : new Date(commercials.updatedAt);
                          const isApprox = !activationEntry;

                          const days = Math.floor((new Date() - activatedAt) / (1000 * 60 * 60 * 24));
                          return (
                            <div className="absolute left-0 top-6 z-20 bg-white border border-blue-200 rounded-xl shadow-lg px-4 py-3 w-56">
                              <div className="space-y-1">
                                <p className="text-xs text-gray-500">Since{isApprox ? ' (approx.)' : ''}</p>
                                <p className="text-sm font-semibold text-gray-800">
                                  {activatedAt.toLocaleDateString('en-IN', {
                                    day: '2-digit', month: 'short', year: 'numeric'
                                  })}
                                </p>
                                <p className="text-xs font-medium text-blue-600">
                                  {days === 0 ? 'Started today' : `${days} day${days > 1 ? 's' : ''} ago`}
                                </p>
                              </div>
                            </div>
                          );
                        })()}
                      </div>
                    )}
                  </div>
                )}
                {!isEditing && !commercials?.accountType && (
                  <span className="px-2 py-0.5 rounded-full text-xs font-semibold bg-green-100 text-green-700">Normal Billing</span>
                )}
              </div>

              {!isEditing ? (
                <p className="text-xs text-gray-400">
                  {commercials?.accountType === 'CLOSED'
                    ? '🔒 This enterprise is permanently closed. Billing is disabled and no further changes can be made.'
                    : commercials?.accountType === 'ZEEROW'
                    ? '🟣 This enterprise is billed under Zeerow. Standard commercial config does not apply — manage billing from the Zeerow section.'
                    : commercials?.accountType
                    ? `⚠️ Billing is DISABLED for this enterprise (${ACCOUNT_TYPES.find(t => t.value === commercials.accountType)?.desc || ''})`
                    : 'Billing runs normally each month for this enterprise.'}
                </p>
              ) : (
                <>
                  <p className="text-xs text-gray-400 mb-3">Select one to disable billing for this enterprise. Click again to deselect.</p>
                  <div className="grid grid-cols-2 gap-3">
                    {ACCOUNT_TYPES.map(type => {
                      const isSelected = accountType === type.value;
                      const colorMap = {
                        blue:   isSelected ? 'border-blue-500 bg-blue-50 ring-2 ring-blue-200'     : 'border-gray-200 hover:border-blue-300 hover:bg-blue-50/50',
                        purple: isSelected ? 'border-purple-500 bg-purple-50 ring-2 ring-purple-200' : 'border-gray-200 hover:border-purple-300 hover:bg-purple-50/50',
                        orange: isSelected ? 'border-orange-500 bg-orange-50 ring-2 ring-orange-200' : 'border-gray-200 hover:border-orange-300 hover:bg-orange-50/50',
                        red:    isSelected ? 'border-red-500 bg-red-50 ring-2 ring-red-200'         : 'border-gray-200 hover:border-red-300 hover:bg-red-50/50',
                        green:  isSelected ? 'border-green-500 bg-green-50 ring-2 ring-green-200'   : 'border-gray-200 hover:border-green-300 hover:bg-green-50/50',
                        gray:   isSelected ? 'border-gray-500 bg-gray-100 ring-2 ring-gray-300'     : 'border-gray-200 hover:border-gray-400 hover:bg-gray-50/50',
                      };
                      const labelColorMap = {
                        blue:   isSelected ? 'text-blue-700'   : 'text-gray-700',
                        purple: isSelected ? 'text-purple-700' : 'text-gray-700',
                        orange: isSelected ? 'text-orange-700' : 'text-gray-700',
                        red:    isSelected ? 'text-red-700'    : 'text-gray-700',
                        green:  isSelected ? 'text-green-700'  : 'text-gray-700',
                        gray:   isSelected ? 'text-gray-700'   : 'text-gray-700',
                      };
                      return (
                        <button
                          key={type.value}
                          type="button"
                          onClick={() => setAccountType(isSelected ? '' : type.value)}
                          className={`relative flex items-start gap-3 p-3 rounded-xl border-2 transition-all text-left ${colorMap[type.color]}`}
                        >
                          <div className={`mt-0.5 w-4 h-4 rounded-full border-2 flex-shrink-0 flex items-center justify-center ${
                            isSelected
                              ? `border-${type.color}-500 bg-${type.color}-500`
                              : 'border-gray-300'
                          }`}>
                            {isSelected && <div className="w-2 h-2 rounded-full bg-white" />}
                          </div>
                          <div>
                            <p className={`text-sm font-semibold ${labelColorMap[type.color]}`}>{type.label}</p>
                            <p className="text-xs text-gray-400 mt-0.5">{type.desc}</p>
                          </div>
                        </button>
                      );
                    })}
                  </div>
                  {accountType === 'CLOSED' && (
                    <div className="mt-3 space-y-2">
                      <div>
                        <label className="block text-xs font-medium text-gray-600 mb-1">
                          Closed On (can be a past date)
                        </label>
                        <input
                          type="date"
                          value={closedAt}
                          min={createdOn ? createdOn.substring(0, 10) : undefined}
                          max={new Date().toISOString().split('T')[0]}
                          onChange={(e) => setClosedAt(e.target.value)}
                          className="border border-gray-300 rounded-lg px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-gray-400/40"
                        />
                      </div>
                      <div className="flex items-start gap-2 bg-red-50 border border-red-200 rounded-lg px-3 py-2">
                        <span className="text-red-500 text-sm mt-0.5">⚠️</span>
                        <p className="text-xs text-red-700 font-medium leading-snug">
                          This is irreversible. Once saved as Closed, this enterprise cannot be edited again.
                        </p>
                      </div>
                    </div>
                  )}
                  {accountType && accountType !== 'CLOSED' && (
                    <p className="mt-2 text-xs text-amber-600 font-medium">
                      ⚠️ Billing will be skipped for this enterprise during bulk generation.
                    </p>
                  )}
                </>
              )}
            </div>

            {/* Change Reason (visible only in edit mode) */}
            {isEditing && (
              <div className="mb-6">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Change Reason (Optional)
                </label>
                <textarea
                  value={changeReason}
                  onChange={(e) => setChangeReason(e.target.value)}
                  placeholder="Why are you making these changes?"
                  rows="2"
                  className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary/30"
                />
              </div>
            )}

            {/* Last Updated Info */}
            {!isEditing && (
              <div className="text-xs text-gray-400 mb-6">
                <p>Last updated by <span className="font-medium">{commercials.updatedByName}</span> on {new Date(commercials.updatedAt).toLocaleString()}</p>
              </div>
            )}

            {/* Action Buttons */}
            {isEditing && (
              <div className="flex gap-3">
                <button
                  onClick={handleSave}
                  disabled={saving}
                  className="px-4 py-2 bg-primary text-white rounded-lg text-sm font-medium hover:bg-blue-700 disabled:opacity-50 transition-colors"
                >
                  {saving ? 'Saving...' : 'Save Changes'}
                </button>
                <button
                  onClick={handleCancel}
                  className="px-4 py-2 border border-gray-200 text-gray-600 rounded-lg text-sm font-medium hover:bg-gray-50 transition-colors"
                >
                  Cancel
                </button>
              </div>
            )}
          </>
        ) : (
          <div className="text-center py-8">
            <p className="text-gray-500 mb-4">No billing configuration exists for this enterprise yet.</p>
            <button
              onClick={async () => {
                try {
                  const response = await api.post(`/api/admin/enterprises/${enterpriseId}/commercials`, {
                    perUserPerMonthMaker: 0,
                    perUserPerMonthAdmin: 0,
                    loadingChargesPercent: 0,
                    upiChargesPerTransaction: 0,
                    additionalCharge: 0,
                    perUserMonthlyMmg: 0,
                    perMonthLoadingMmg: 0,
                    perMonthOverallMmg: 0,
                    accountType: null,
                  });
                  setCommercials(response.data);
                  toast.success('Commercials created successfully');
                  await fetchHistory();
                  if (onSave) onSave();
                } catch (err) {
                  toast.error(err.response?.data?.error || 'Failed to create commercials');
                }
              }}
              className="px-4 py-2 bg-primary text-white rounded-lg text-sm font-medium hover:bg-blue-700"
            >
              Create Configuration
            </button>
          </div>
        )}
      </div>

      {/* History Table */}
      {history.length > 0 && (
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
          <div className="px-5 py-4 border-b border-gray-100">
            <p className="text-sm font-semibold text-gray-700">Change History</p>
            <p className="text-xs text-gray-400 mt-1">Count: {history.length}</p>
          </div>

          <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="text-xs text-gray-400 uppercase bg-gray-50">
              <tr>
                <th className="text-left px-5 py-3 font-medium whitespace-nowrap">Date & Time</th>
                <th className="text-left px-5 py-3 font-medium whitespace-nowrap">Changed By</th>
                <th className="text-left px-5 py-3 font-medium whitespace-nowrap">Maker (₹)</th>
                <th className="text-left px-5 py-3 font-medium whitespace-nowrap">Maker Slabs</th>
                <th className="text-left px-5 py-3 font-medium whitespace-nowrap">Admin (₹)</th>
                <th className="text-left px-5 py-3 font-medium whitespace-nowrap">Loading (%)</th>
                <th className="text-left px-5 py-3 font-medium whitespace-nowrap">UPI (₹)</th>
                <th className="text-left px-5 py-3 font-medium whitespace-nowrap">Free UPI (txns)</th>
                <th className="text-left px-5 py-3 font-medium whitespace-nowrap">Free Admins</th>
                <th className="text-left px-5 py-3 font-medium whitespace-nowrap">Additional (₹)</th>
                <th className="text-left px-5 py-3 font-medium whitespace-nowrap">User MMG (₹)</th>
                <th className="text-left px-5 py-3 font-medium whitespace-nowrap">Loading MMG (₹)</th>
                <th className="text-left px-5 py-3 font-medium whitespace-nowrap">Overall MMG (₹)</th>
                <th className="text-left px-5 py-3 font-medium whitespace-nowrap">Classification</th>
                <th className="text-left px-5 py-3 font-medium whitespace-nowrap">Reason</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-50">
              {paginatedHistory.map((entry) => (
                <tr key={entry.id} className="hover:bg-gray-50">
                  <td className="px-5 py-3 text-gray-700">
                    {new Date(entry.createdAt).toLocaleString()}
                  </td>
                  <td className="px-5 py-3 text-gray-700">{entry.changedByName}</td>
                  <td className="px-5 py-3 text-gray-700">
                    ₹{parseFloat(entry.perUserPerMonthMaker || 0).toFixed(2)}
                  </td>
                  <td className="px-5 py-3">
                    {entry.makerSlabs && entry.makerSlabs.length > 0 ? (
                      <div className="flex flex-col gap-0.5">
                        {entry.makerSlabs.map((s, i) => (
                          <span key={i} className="text-xs text-gray-700 whitespace-nowrap">
                            {s.fromCount}–{s.toCount !== null && s.toCount !== undefined ? s.toCount : '∞'}:{' '}
                            {s.chargeType === 'FIXED'
                              ? `Fixed ₹${parseFloat(s.rate || 0).toFixed(2)}`
                              : `₹${parseFloat(s.rate || 0).toFixed(2)}/user`}
                          </span>
                        ))}
                      </div>
                    ) : (
                      <span className="text-xs text-gray-400 bg-gray-100 px-2 py-0.5 rounded-full">Flat Rate</span>
                    )}
                  </td>
                  <td className="px-5 py-3 text-gray-700">
                    ₹{parseFloat(entry.perUserPerMonthAdmin || 0).toFixed(2)}
                  </td>
                  <td className="px-5 py-3 text-gray-700">
                    {parseFloat(entry.loadingChargesPercent || 0).toFixed(2)}%
                  </td>
                  <td className="px-5 py-3 text-gray-700">
                    ₹{parseFloat(entry.upiChargesPerTransaction || 0).toFixed(2)}
                  </td>
                  <td className="px-5 py-3 text-gray-700">
                    {entry.freeUpiTransactions ?? 0} txns
                  </td>
                  <td className="px-5 py-3 text-gray-700">
                    {entry.freeAdminUsers ?? 0} admins
                  </td>
                  <td className="px-5 py-3 text-gray-700">
                    ₹{parseFloat(entry.additionalCharge || 0).toFixed(2)}
                  </td>
                  <td className="px-5 py-3 text-gray-700">
                    ₹{parseFloat(entry.perUserMonthlyMmg || 0).toFixed(2)}
                  </td>
                  <td className="px-5 py-3 text-gray-700">
                    ₹{parseFloat(entry.perMonthLoadingMmg || 0).toFixed(2)}
                  </td>
                  <td className="px-5 py-3 text-gray-700">
                    ₹{parseFloat(entry.perMonthOverallMmg || 0).toFixed(2)}
                  </td>
                  <td className="px-5 py-3">
                    {entry.accountType ? (
                      <div className="flex flex-col items-start gap-1">
                        <span className={`inline-flex flex-col items-center px-3 py-1.5 rounded-lg text-xs font-semibold leading-snug ${
                          entry.accountType === 'DEMO'         ? 'bg-blue-100 text-blue-700' :
                          entry.accountType === 'ZEEROW'       ? 'bg-purple-100 text-purple-700' :
                          entry.accountType === 'TESTING'      ? 'bg-orange-100 text-orange-700' :
                          entry.accountType === 'ANNUAL'       ? 'bg-green-100 text-green-700' :
                          entry.accountType === 'CLOSED'       ? 'bg-gray-200 text-gray-600' :
                          'bg-red-100 text-red-700'
                        }`}>
                          {entry.accountType === 'DEMO'    ? <><span>Demo</span><span>Account</span></> :
                           entry.accountType === 'ZEEROW'  ? <><span>Zeerow</span><span>Account</span></> :
                           entry.accountType === 'TESTING' ? <><span>Testing</span><span>Account</span></> :
                           entry.accountType === 'ANNUAL'  ? <><span>Annual</span><span>Charges</span></> :
                           entry.accountType === 'CLOSED'  ? <><span>Closed</span><span>Account</span></> :
                           <><span>Inconsistent</span><span>Commercials</span></>}
                        </span>
                        {entry.accountType === 'CLOSED' && entry.closedAt && (
                          <span className="text-xs text-gray-400">
                            {new Date(entry.closedAt).toLocaleDateString('en-IN', {
                              day: '2-digit', month: 'short', year: 'numeric'
                            })}
                          </span>
                        )}
                      </div>
                    ) : (
                      <span className="text-xs text-gray-400">—</span>
                    )}
                  </td>
                  <td className="px-5 py-3 text-gray-600 text-xs">
                    {entry.changeReason || '—'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="flex items-center justify-between px-5 py-3 border-t border-gray-100">
              <p className="text-xs text-gray-400">Page {currentPage} of {totalPages}</p>
              <div className="flex items-center gap-1">
                <button
                  disabled={currentPage === 1}
                  onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                  className="px-2 py-1 text-xs border border-gray-200 rounded-lg disabled:opacity-50 hover:bg-gray-50"
                >
                  Previous
                </button>
                {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                  const p = Math.max(1, Math.min(currentPage - 2, totalPages - 4)) + i;
                  return (
                    <button
                      key={p}
                      onClick={() => setCurrentPage(p)}
                      className={`px-2 py-1 text-xs rounded-lg transition-colors ${
                        p === currentPage
                          ? 'bg-primary text-white'
                          : 'border border-gray-200 hover:bg-gray-50'
                      }`}
                    >
                      {p}
                    </button>
                  );
                })}
                <button
                  disabled={currentPage === totalPages}
                  onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                  className="px-2 py-1 text-xs border border-gray-200 rounded-lg disabled:opacity-50 hover:bg-gray-50"
                >
                  Next
                </button>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default Commercials;
